import React, { useState } from 'react';
import { Paper, Typography } from '@mui/material';

import DataGrid, {
  Column,
  Editing,
  Lookup,
  Paging,
  SearchPanel,
  Pager,
  Scrolling,
  Toolbar,
  Item,
  RequiredRule
} from 'devextreme-react/data-grid';
import { SelectBox } from 'devextreme-react';
import { RowUpdatingEvent } from 'devextreme/ui/data_grid';

import '../../../../components/DevExtreme/style.scss'
import { useDxStore } from '../../../../hooks/useDxStore';

import { useAccountsLookupStore } from '../../../../hooks/useAccountsLookupStore';
import { LookupItem, useUsersLookupStore } from '../../../../hooks/useUsersLookupStore';

const defaultPageSize = 10;
const allowedPageSizes = [10, 25, 50];
const defaultSelectedUserInfo: LookupItem = {
  id: null,
  accountId: null,
  userLogin: 'Select ...',
  userAccount: null,
  userEmail: null,
  userName : null
}


export default function UserAccountAccess() {
  const acountAccessStore = useDxStore(
    {
      url: 'user-account-access',
      keyField: 'id',
      dataSourceKey: 'userAccountAccessList',
    }
  );

  const accountsLookupStore = useAccountsLookupStore();
  const usersLookupStore  = useUsersLookupStore();

  const [selectedUserInfo, setSelectedUserInfo] = useState<any>(defaultSelectedUserInfo);
  const [isNewRow, setIsNewRow] = useState<boolean>(false);

  const handleRowUpdating = (e: RowUpdatingEvent<Partial<{id: any}>>) => {
    e.newData = {
      ...e.oldData,
      ...e.newData,
    };
  };

  return (
    <Paper
      sx={{ width: '100%' }}
      elevation={2}
      data-test="user-account-access"
    >
      <DataGrid
        id="userAccountAccessTable"
        data-test="user-account-access-table"
        dataSource={acountAccessStore}
        allowColumnReordering={true}
        rowAlternationEnabled={true}
        onEditingStart={() => {
          setIsNewRow(false);
          setSelectedUserInfo(defaultSelectedUserInfo);
        }}
        repaintChangesOnly={true}
        customizeColumns={(columns) => {
          columns.forEach(col => {
            col.alignment = 'left';
          });
        }}
        showBorders={true}
        onRowUpdating={handleRowUpdating}
        onEditCanceled={() => {
            setIsNewRow(false);
            setSelectedUserInfo(defaultSelectedUserInfo);
          }
        }
        onSaved={() => {
            setIsNewRow(false);
            setSelectedUserInfo(defaultSelectedUserInfo);
          }
        }
        onInitNewRow={() => {
          setIsNewRow(true);
          setSelectedUserInfo(defaultSelectedUserInfo);
        }}
      >
        <Editing
          mode="row"
          allowAdding={true}
          allowDeleting={true}
          allowUpdating={true}
          useIcons={true}
        />
        <Scrolling
          mode="standard"
        />
        <Toolbar
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          elementAttr={{ class: 'dx-toolbar-custom' }}
        >
          <Item
            location="before"
            locateInMenu="never"
          >
            <Typography
              data-test="title-table"
              variant="h5"
              sx={{ marginRight: 2 }}
            >
              WD3 Users Access Configuration
            </Typography>
          </Item>
          <Item
            name="addRowButton"
            data-test={'add-row-button'}
            location="after"
          />
          <Item
            name="searchPanel"
            data-test={'search-panel'}
            location="after"
            cssClass={'dx-searchinput-custom'}
          />
        </Toolbar>
        <SearchPanel
          visible={true}
          highlightCaseSensitive={true}
        />
        <Paging
          defaultPageSize={defaultPageSize}
          enabled={true}
        />
        <Pager
          allowedPageSizes={allowedPageSizes}
          showNavigationButtons={true}
        />
        <Column
          dataField="userId"
          caption="User Login"
          width={225}
          allowEditing={isNewRow}
          calculateDisplayValue={rowData => rowData.userLogin}
          editCellRender={(cellInfo) => (
            <SelectBox
              dataSource={{
                store: usersLookupStore,
                paginate: true,
                pageSize: 10,
              }}
              valueExpr="id"
              displayExpr="userLogin"
              placeholder={selectedUserInfo.userLogin}
              searchEnabled={true}
              dropDownOptions={{ height: 300 }}
              onSelectionChanged={
                (e) => {
                  cellInfo.setValue(e.selectedItem.id, e.selectedItem.userLogin);
                  cellInfo.update({
                    ...cellInfo.row.data,
                    userId: e.selectedItem.id,
                    userLogin: e.selectedItem.userLogin,
                    userName: e.selectedItem.userName,
                    userAccount: e.selectedItem.userAccount,
                    userEmail: e.selectedItem.userEmail,
                  })
                  setSelectedUserInfo(e.selectedItem)
                }
              }
            />
          )}
        >
          <RequiredRule />
        </Column>
        <Column
          dataField="userName"
          caption="User Name"
          allowEditing={false}
          calculateDisplayValue={
            (rowData) => {
              return rowData.userId === selectedUserInfo.id ? selectedUserInfo.userName : rowData.userName
            }
          }
        />
        <Column
          dataField="userAccount"
          caption="User Account"
          allowEditing={false}
          calculateDisplayValue={
            (rowData) => {
              return rowData.userId === selectedUserInfo.id ? selectedUserInfo.userAccount : rowData.userAccount;
            }
          }
        />
        <Column
          dataField="userEmail"
          caption="Email"
          allowEditing={false}
          calculateDisplayValue={
            (rowData) => {
              return rowData.userId === selectedUserInfo.id ? selectedUserInfo.userEmail : rowData.userEmail;
            }
          }
        />
        <Column
          dataField="targetAccountId"
          caption="Target Account"
          allowEditing={isNewRow}
          calculateDisplayValue={(rowData) => rowData.targetAccount}
        >
          <Lookup
            dataSource={{
              store: accountsLookupStore,
              paginate: true,
              pageSize: 10,
            }}
            valueExpr="id"
            displayExpr="name"
          />
          <RequiredRule />
        </Column>
        <Column
          dataField="accessType"
          caption="Access Type"
        >
          <Lookup
            dataSource={
              [{ accessType: 0, name: 'Granted' }, { accessType: 1, name: 'Restricted' }]
            }
            displayExpr="name"
            valueExpr="accessType"
          />
          <RequiredRule />
        </Column>
        <Column
          type='buttons'
          caption={'Actions'}
        >
        </Column>
      </DataGrid>
    </Paper>
  );
}
